/**
 * Click-to-place for Quick Chat / Voice / Comment from toolbar and explorer
 * rail. Right-click insert starts the composer immediately at the cursor
 * instead.
 *
 * The drop is committed by the placement overlay in `CanvasPage`, not by
 * `onPaneClick`: a node under the cursor takes the pane's click, and a drop
 * aimed at a node is the case that has to work — it is what anchors the pin.
 */

import { CANVAS_SNAP, snapToGrid } from "@/pages/canvas/canvas-controls.config";
import { useReactFlow } from "@xyflow/react";
import {
  type RefObject,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  placementForDrop,
  type ThreadPlacement,
} from "../comments/threadPlacement";
import { useCanvasUi } from "../context/CanvasUiContext";
import { nodeBounds } from "../utils/nodeBounds";

export type DeferredQuickComposeMode = "quick-chat" | "quick-voice" | "comment";

/** Matches ChatSessionNode bubble — constant 40×40 screen px via inverse zoom. */
export const QUICK_COMPOSE_GHOST_ICON_SIZE = 40;

export interface DeferredQuickComposeGhost {
  /** Snapped flow position of the bubble center (follows the cursor). */
  center: { x: number; y: number };
  mode: DeferredQuickComposeMode;
  /** Comment mode: the node the drop would anchor to, for the snap highlight. */
  snapNodeId?: string;
}

interface Position {
  x: number;
  y: number;
}

export interface UseDeferredQuickChatPlacementParams {
  lastCursorScreenPosRef: RefObject<Position | null>;
  onStartQuickChat: (screenPosition: { x: number; y: number }) => void;
  onStartQuickVoice: (screenPosition: { x: number; y: number }) => void;
  /** The ghost already resolved the anchor, so the drop needs no second gesture. */
  onStartComment: (placement: ThreadPlacement) => void;
}

export function useDeferredQuickChatPlacement({
  lastCursorScreenPosRef,
  onStartQuickChat,
  onStartQuickVoice,
  onStartComment,
}: UseDeferredQuickChatPlacementParams) {
  const { screenToFlowPosition, getNodes } = useReactFlow();
  const { reactFlowWrapperRef } = useCanvasUi();
  const [pending, setPending] = useState<DeferredQuickComposeMode | null>(null);
  const [ghostPosition, setGhostPosition] = useState<Position | null>(null);

  const [snapX, snapY] = CANVAS_SNAP.grid;

  const snapFlowPoint = useCallback(
    (screenPos: Position): Position => {
      const flow = screenToFlowPosition(screenPos);
      return {
        x: snapToGrid(flow.x, snapX),
        y: snapToGrid(flow.y, snapY),
      };
    },
    [screenToFlowPosition, snapX, snapY],
  );

  // The pin lands where the cursor is: a comment is an annotation, not a node,
  // so it does not join the lattice.
  const commentPlacementAt = useCallback(
    (screenPos: Position): ThreadPlacement =>
      placementForDrop(
        screenToFlowPosition(screenPos),
        getNodes().map(nodeBounds),
      ),
    [screenToFlowPosition, getNodes],
  );

  const cancelPlacement = useCallback(() => {
    setPending(null);
    setGhostPosition(null);
  }, []);

  const beginPlacement = useCallback(
    (mode: DeferredQuickComposeMode) => {
      setPending(mode);
      const cursor = lastCursorScreenPosRef.current;
      const point = mode === "comment" ? screenToFlowPosition : snapFlowPoint;
      setGhostPosition(cursor ? point(cursor) : null);
    },
    [lastCursorScreenPosRef, snapFlowPoint, screenToFlowPosition],
  );

  const ghost = useMemo((): DeferredQuickComposeGhost | null => {
    if (!pending || !ghostPosition) return null;
    if (pending !== "comment") return { center: ghostPosition, mode: pending };
    const snapped = placementForDrop(ghostPosition, getNodes().map(nodeBounds));
    return {
      center: ghostPosition,
      mode: pending,
      snapNodeId: snapped.anchorNodeId,
    };
  }, [pending, ghostPosition, getNodes]);

  useEffect(() => {
    if (!pending) return;

    const handler = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      e.preventDefault();
      cancelPlacement();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [pending, cancelPlacement]);

  useEffect(() => {
    if (!pending) return;

    // A comment pin never joins the lattice, so its ghost must preview the
    // unsnapped point the drop will actually use.
    const point = pending === "comment" ? screenToFlowPosition : snapFlowPoint;

    const handler = (e: MouseEvent) => {
      const wrapper = reactFlowWrapperRef.current;
      if (wrapper && !wrapper.contains(e.target as globalThis.Node)) return;
      setGhostPosition(point({ x: e.clientX, y: e.clientY }));
    };

    window.addEventListener("mousemove", handler);
    const cursor = lastCursorScreenPosRef.current;
    if (cursor) {
      setGhostPosition(point(cursor));
    }

    return () => window.removeEventListener("mousemove", handler);
  }, [
    pending,
    reactFlowWrapperRef,
    lastCursorScreenPosRef,
    snapFlowPoint,
    screenToFlowPosition,
  ]);

  const commitAtScreen = useCallback(
    (clientX: number, clientY: number) => {
      if (!pending) return;
      // Chat session nodes anchor top-left; offset so the rust bubble center
      // lands on the click point (matches the centered ghost preview).
      const half = QUICK_COMPOSE_GHOST_ICON_SIZE / 2;
      const screenPos = { x: clientX - half, y: clientY - half };
      if (pending === "comment") {
        onStartComment(commentPlacementAt({ x: clientX, y: clientY }));
      } else if (pending === "quick-chat") {
        onStartQuickChat(screenPos);
      } else {
        onStartQuickVoice(screenPos);
      }
      cancelPlacement();
    },
    [
      pending,
      onStartQuickChat,
      onStartQuickVoice,
      onStartComment,
      commentPlacementAt,
      cancelPlacement,
    ],
  );

  return {
    isPlacing: pending != null,
    ghost,
    beginPlacement,
    cancelPlacement,
    commitAtScreen,
  };
}
